import React, { useMemo , useState } from "react";
import { useNavigate } from "react-router-dom";
import { TbArrowBackUp } from "react-icons/tb";
import apiClient from "../api/apiClient";
import { handleErrors } from "../modules/handleErrors";
import LoadingSpinner from "../components/LoadingSpinner";
import InfiniteScroll from "../components/InfiniteScroll";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import RecentSalesList from "../components/RecentSalesList";

const AllSales = () => {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const pageSize = 10;

  // Fetch function for infinite scroll
  const fetchSales = useMemo(() => {
    return async (page) => {
      const params = new URLSearchParams();
      params.append("data", "s");
      params.append("page", page);
      params.append("page_size", pageSize);

      // Add date filters if selected
      if (startDate) {
        params.append("start_date", dayjs(startDate).format("YYYY-MM-DD"));
      }
      if (endDate) {
        params.append("end_date", dayjs(endDate).format("YYYY-MM-DD"));
      }

      const result = await apiClient.get(`/sales/?${params.toString()}`);
      
      if (!result.ok) {
        handleErrors(result.data);
        return { ok: false, data: { results: [], count: 0 } };
      }

      return result;
    };
  }, [startDate, endDate, pageSize]);

  // Use infinite scroll hook
  const { data: saleItems, loading, hasMore, loadMore, totalCount } = useInfiniteScroll(
    fetchSales,
    [startDate, endDate],
    pageSize
  );

  const handleDateFilterChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const clearFilters = () => {
    setStartDate(null);
    setEndDate(null);
  };

  return (
    <>
      <div className="container py-3 px-3 px-md-4">
        <div className="d-flex align-items-center gap-3 mb-4">
          <button
            type="button"
            className="btn btn-light border shadow-sm rounded-circle p-0 d-flex align-items-center justify-content-center flex-shrink-0"
            onClick={() => navigate(-1)}
            aria-label="Go back"
            style={{ width: 42, height: 42 }}
          >
            <TbArrowBackUp style={{ fontSize: "26px" }} />
          </button>
          <h2 className="mb-0" style={{ color: "#000000e6" }}>
            All Sales
          </h2>
        </div>

        <section className="mb-4">
          <div className="row d-flex g-3 align-items-end">
            <div className="d-flex align-items-center gap-3">
              <label htmlFor="all-sales-start-date" className="form-label mb-2">
                Start Date
              </label>
              <DatePicker
                id="all-sales-start-date"
                selected={startDate}
                onChange={(date) => handleDateFilterChange(date, endDate)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                placeholderText="Select Start Date"
                className="form-control w-100"
                maxDate={endDate || new Date()}
              />
            </div>
            <div className="d-flex align-items-center gap-3">
              <label htmlFor="all-sales-end-date" className="form-label mb-2">
                End Date
              </label>
              <DatePicker
                id="all-sales-end-date"
                selected={endDate}
                onChange={(date) => handleDateFilterChange(startDate, date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                placeholderText="Select End Date"
                className="form-control w-100"
                minDate={startDate}
                maxDate={new Date()}
              />
            </div>
            {(startDate || endDate) && (
              <div className="col-12 col-sm-auto">
                <button
                  type="button"
                  className="btn btn-outline-secondary w-100 w-sm-auto"
                  onClick={clearFilters}
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="my-5 pt-2">
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <h3 className="mb-0">Sales History</h3>
            {totalCount > 0 && (
              <p className="mb-0 text-muted">
                Total: {totalCount} sale{totalCount !== 1 ? "s" : ""}
              </p>
            )}
          </div>
          <div>
            {loading && saleItems?.length === 0 ? (
              <div className="d-flex justify-content-center mt-5">
                <LoadingSpinner />
              </div>
            ) : saleItems?.length === 0 ? (
              <p>No sales found for the selected criteria.</p>
            ) : (
              <InfiniteScroll
                onLoadMore={loadMore}
                hasMore={hasMore}
                loading={loading}
              >
                <RecentSalesList
                  items={saleItems}
                  emptyMessage="No sales found for the selected criteria."
                />
              </InfiniteScroll>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default AllSales;
